import { z } from "zod";

const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

const rankSchema = z.object({
  rankId: z.string().min(1),
  name: z.string().trim().min(1).max(80),
  color: colorSchema,
  backgroundColor: colorSchema,
  icon: z.string().trim().max(16).optional(),
  imageMediaId: z.string().min(1).optional(),
  requiredProgress: z.number().int().nonnegative().max(100000),
});

const jokerSchema = z.object({
  jokerId: z.string().min(1),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  color: colorSchema,
  icon: z.string().trim().max(16).optional(),
  imageMediaId: z.string().min(1).optional(),
  type: z.enum(["fifty-fifty", "custom", "pass-question"]),
  startingCount: z.number().int().nonnegative().max(100),
});

export const answerOptionSchema = z.object({
  optionId: z.string().min(1),
  text: z.string().min(1).max(1000),
  isCorrect: z.boolean(),
  aliases: z.array(z.string().min(1).max(200)).max(20).optional(),
  mediaId: z.string().optional(),
});

export const quizQuestionSchema = z
  .object({
    questionId: z.string().min(1),
    prompt: z.string().min(1).max(4000),
    answers: z.array(answerOptionSchema).min(1).max(20),
    adminNotes: z.string().max(4000).optional(),
    timerEnabled: z.boolean(),
    timerSeconds: z.number().int().min(5).max(3600),
    points: z.number().int().min(0).max(100000),
    media: z.object({
      imageMediaId: z.string().optional(),
      audioMediaId: z.string().optional(),
      backgroundMediaId: z.string().optional(),
      backgroundColor: colorSchema.optional(),
      imageSize: z.enum(["normal", "large", "fullscreen"]).optional(),
    }),
    fiftyFiftyRemoveCount: z.number().int().min(1).max(18),
    questionType: z.enum(["normal", "bonus"]).default("normal"),
    bonusReward: z
      .object({
        type: z.enum(["joker", "life", "points"]),
        amount: z.number().int().positive().max(100000),
        jokerId: z.string().min(1).optional(),
      })
      .optional(),
    answerMode: z.enum(["multiple-choice", "free-text"]).default("multiple-choice"),
    selectionMode: z.enum(["single", "multiple"]).default("single"),
    evaluationMode: z.enum(["automatic", "manual"]).default("manual"),
  })
  .refine((question) => question.answerMode !== "multiple-choice" || question.answers.length >= 2, {
    message: "Multiple-Choice-Fragen benötigen mindestens zwei Antworten.",
    path: ["answers"],
  })
  .refine((question) => question.answers.some((answer) => answer.isCorrect), {
    message: "Mindestens eine Antwort muss richtig sein.",
    path: ["answers"],
  })
  .refine(
    (question) =>
      question.answerMode !== "multiple-choice" ||
      question.selectionMode !== "single" ||
      question.answers.filter((answer) => answer.isCorrect).length === 1,
    {
      message: "Single-Choice-Fragen benötigen genau eine richtige Antwort.",
      path: ["answers"],
    },
  );

export const classicQuestionSchema = quizQuestionSchema;

const mediaAssetSchema = z.object({
  mediaId: z.string().min(1),
  kind: z.enum(["image", "audio", "background", "icon"]),
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp", "audio/mpeg", "audio/ogg", "audio/wav", "audio/x-wav"]),
  bytes: z.number().int().positive().max(15 * 1024 * 1024),
});

const quizBase = {
  formatVersion: z.literal(1),
  quizId: z.string().min(1),
  moduleVersion: z.literal(1),
  title: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  updatedAt: z.string().datetime(),
  theme: z.object({
    primaryColor: colorSchema,
    accentColor: colorSchema,
    backgroundColor: colorSchema,
  }),
  mediaAssets: z.array(mediaAssetSchema).max(500),
  allowSelfTeamSelection: z.boolean(),
  allowParticipantJokers: z.boolean().default(false),
  autoOpenBuzzer: z.boolean().default(false),
  scoringMode: z.enum(["points", "rank-progress"]).default("points"),
  ranks: z.array(rankSchema).max(100).default([]),
  jokers: z.array(jokerSchema).max(100).default([]),
  usesLives: z.boolean().default(false),
  defaultLives: z.number().int().min(1).max(20).default(3),
};

export const classicQuizSchema = z.object({
  ...quizBase,
  moduleId: z.literal("classic"),
  questions: z.array(quizQuestionSchema).min(1).max(500),
});

export const weakestLinkQuizSchema = z.object({
  ...quizBase,
  usesLives: z.literal(true).default(true),
  moduleId: z.literal("weakest-link"),
  questions: z.array(quizQuestionSchema).min(1).max(500),
  startingLives: z.number().int().min(1).max(20),
  questionOrder: z.enum(["fixed", "random"]).default("fixed"),
});

export const quizBoardQuizSchema = z.object({
  ...quizBase,
  moduleId: z.literal("quizboard"),
  categories: z
    .array(
      z.object({
        categoryId: z.string().min(1),
        name: z.string().min(1).max(80),
        cells: z
          .array(
            z.object({
              cellId: z.string().min(1),
              points: z.number().int().min(0).max(100000),
              special: z.enum(["none", "double"]),
              question: quizQuestionSchema,
            }),
          )
          .min(1)
          .max(20),
      }),
    )
    .min(1)
    .max(20),
});

export const quizSetSchema = z.discriminatedUnion("moduleId", [
  classicQuizSchema,
  weakestLinkQuizSchema,
  quizBoardQuizSchema,
]).superRefine((quiz, context) => {
  if (quiz.moduleId === "classic" && quiz.scoringMode === "rank-progress" && quiz.ranks.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["ranks"],
      message: "Für Rangfortschritt muss mindestens ein Rang definiert sein.",
    });
  }
  const jokerIds = new Set(quiz.jokers.map((joker) => joker.jokerId));
  const questions =
    quiz.moduleId === "quizboard"
      ? quiz.categories.flatMap((category) => category.cells.map((cell) => cell.question))
      : quiz.questions;
  for (const [index, question] of questions.entries()) {
    if (
      question.questionType === "bonus" &&
      question.bonusReward?.type === "joker" &&
      (!question.bonusReward.jokerId || !jokerIds.has(question.bonusReward.jokerId))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["questions", index, "bonusReward", "jokerId"],
        message: "Die Bonusfrage verweist auf keinen vorhandenen Joker.",
      });
    }
    if (question.bonusReward?.type === "life" && !quiz.usesLives) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["questions", index, "bonusReward"],
        message: "Lebensbelohnungen benötigen einen aktivierten Lebensmodus.",
      });
    }
  }
  const totalMediaBytes = quiz.mediaAssets.reduce((total, asset) => total + asset.bytes, 0);
  if (totalMediaBytes > 100 * 1024 * 1024) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["mediaAssets"],
      message: "Das Medienlimit von 100 MB pro Quiz ist überschritten.",
    });
  }
});

export const createRoomRequestSchema = z.object({
  quiz: quizSetSchema,
});

export const joinRoomRequestSchema = z.object({
  roomCode: z.string().trim().length(6),
  displayName: z.string().trim().min(1).max(24),
});

export const exchangeLinkRequestSchema = z.object({
  secret: z.string().min(32).max(512),
});

export const socketTicketRequestSchema = z.object({
  lastRevision: z.number().int().nonnegative().default(0),
});

export const feedbackRequestSchema = z.object({
  audience: z.enum(["host", "participant"]),
  category: z.enum(["bug", "improvement", "joker-idea", "mode-idea"]),
  title: z.string().trim().min(3).max(160),
  message: z.string().trim().min(5).max(4000),
  mode: z.enum(["classic", "weakest-link", "quizboard"]),
  roomId: z.string().optional(),
}).superRefine((feedback, context) => {
  if (feedback.audience === "participant" && ["joker-idea", "mode-idea"].includes(feedback.category)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["category"],
      message: "Diese Kategorie ist nur für Host-Feedback verfügbar.",
    });
  }
});

export const commandEnvelopeSchema = z.object({
  type: z.literal("command"),
  commandId: z.string().min(1),
  expectedRevision: z.number().int().nonnegative().optional(),
  command: z.discriminatedUnion("type", [
    z.object({ type: z.literal("START_GAME") }),
    z.object({ type: z.literal("NEXT_QUESTION") }),
    z.object({ type: z.literal("PREVIOUS_QUESTION") }),
    z.object({ type: z.literal("SET_PARTICIPANT_ANSWERS"), enabled: z.boolean() }),
    z.object({
      type: z.literal("CREATE_TEAM"),
      name: z.string().min(1).max(32),
      color: colorSchema,
      logoMediaId: z.string().min(1).optional(),
    }),
    z.object({
      type: z.literal("UPDATE_TEAM"),
      teamId: z.string().min(1),
      name: z.string().min(1).max(32).optional(),
      color: colorSchema.optional(),
      logoMediaId: z.string().min(1).optional(),
    }),
    z.object({ type: z.literal("ASSIGN_PLAYER"), playerId: z.string().min(1), teamId: z.string().optional() }),
    z.object({ type: z.literal("OPEN_BUZZER") }),
    z.object({ type: z.literal("CLOSE_BUZZER") }),
    z.object({ type: z.literal("BUZZ") }),
    z.object({ type: z.literal("ADD_SCORE"), target: z.enum(["player", "team"]), targetId: z.string(), delta: z.number().int() }),
    z.object({
      type: z.literal("ADD_RANK_PROGRESS"),
      target: z.enum(["player", "team"]),
      targetId: z.string(),
      delta: z.number().int(),
    }),
    z.object({ type: z.literal("ADD_LIFE"), target: z.enum(["player", "team"]), targetId: z.string(), delta: z.number().int() }),
    z.object({
      type: z.literal("AWARD_QUESTION"),
      target: z.enum(["player", "team"]),
      targetId: z.string().min(1),
      correct: z.boolean(),
    }),
    z.object({
      type: z.literal("ADJUST_JOKER"),
      target: z.enum(["player", "team"]),
      targetId: z.string().min(1),
      jokerId: z.string().min(1),
      delta: z.number().int().min(-100).max(100),
    }),
    z.object({
      type: z.literal("USE_JOKER"),
      target: z.enum(["player", "team"]),
      targetId: z.string().min(1),
      jokerId: z.string().min(1),
    }),
    z.object({ type: z.literal("REQUEST_JOKER"), jokerId: z.string().min(1) }),
    z.object({
      type: z.literal("REVIEW_JOKER_REQUEST"),
      requestId: z.string().min(1),
      accepted: z.boolean(),
      targetPlayerId: z.string().min(1).optional(),
    }),
    z.object({ type: z.literal("ELIMINATE_PLAYER"), playerId: z.string().min(1) }),
    z.object({ type: z.literal("RESTORE_PLAYER"), playerId: z.string().min(1) }),
    z.object({
      type: z.literal("SUBMIT_ANSWER"),
      optionIds: z.array(z.string()).min(1).max(20).optional(),
      text: z.string().trim().min(1).max(2000).optional(),
    }),
    z.object({
      type: z.literal("REVIEW_SUBMISSION"),
      submissionId: z.string().min(1),
      evaluation: z.enum(["correct", "incorrect", "partial"]),
      releaseToStream: z.boolean().optional(),
    }),
    z.object({
      type: z.literal("SET_SUBMISSION_VISIBILITY"),
      submissionId: z.string().min(1),
      releasedToStream: z.boolean(),
    }),
    z.object({
      type: z.literal("SET_ANSWER_MARK"),
      optionId: z.string().min(1),
      status: z.enum(["correct", "incorrect", "mentioned", "revealed"]).optional(),
      releasedToParticipants: z.boolean().optional(),
      releasedToStream: z.boolean().optional(),
    }),
    z.object({
      type: z.literal("SHOW_SOLUTION"),
      releasedToParticipants: z.boolean().optional(),
      releasedToStream: z.boolean().optional(),
    }),
    z.object({
      type: z.literal("RESOLVE_QUESTION"),
      releasedToParticipants: z.boolean().optional(),
      releasedToStream: z.boolean().optional(),
    }),
    z.object({
      type: z.literal("MANUAL_SOLVE"),
      optionId: z.string().min(1),
      target: z.enum(["player", "team"]).optional(),
      targetId: z.string().min(1).optional(),
      markReleasedToParticipants: z.boolean().optional(),
      markReleasedToStream: z.boolean().optional(),
      releasedToParticipants: z.boolean().optional(),
      releasedToStream: z.boolean().optional(),
    }).refine((value) => Boolean(value.target) === Boolean(value.targetId), {
      message: "Wertungsziel und Ziel-ID müssen gemeinsam gesetzt werden.",
      path: ["targetId"],
    }),
    z.object({ type: z.literal("RESET_QUESTION"), revertEffects: z.boolean() }),
    z.object({ type: z.literal("START_TIMER"), durationSeconds: z.number().int().min(1).max(3600).optional() }),
    z.object({ type: z.literal("STOP_TIMER") }),
    z.object({ type: z.literal("EXPIRE_TIMER") }),
    z.object({ type: z.literal("USE_FIFTY_FIFTY") }),
    z.object({ type: z.literal("SKIP_FIFTY_FIFTY_ANIMATION") }),
    z.object({ type: z.literal("OPEN_BOARD_CELL"), cellId: z.string().min(1) }),
    z.object({ type: z.literal("MARK_BOARD_CELL_USED"), cellId: z.string().min(1) }),
    z.object({ type: z.literal("RETURN_TO_BOARD") }),
    z.object({ type: z.literal("PLAY_AUDIO"), mediaId: z.string().min(1) }),
    z.object({ type: z.literal("STOP_AUDIO") }),
    z.object({ type: z.literal("UNDO") }),
  ]),
});
